import React, { useState } from "react";
import { Flex, Button, Typography } from "antd";
import { ExportOutlined, LinkOutlined, CheckOutlined } from "@ant-design/icons";
import EmailInputWithTags from "./emailInputWithTags";
import "./styles.less";
import { type ShareSingleReportProps } from "./types";
import { TrialReportAmplitudeEvent } from "../../../shared/utils/amplitudeEvents";
import { formatTranslation } from "../../../shared/utils/translationUtils";
import { t } from "i18next";
import { useTrackingEvent } from "../../../shared/custom-hooks/useTracking";

const { Text } = Typography;
const BaseURL = `${process.env.REACT_APP_REPORT_URL ?? ""}report?id=`;
export const ShareSingleReport: React.FC<ShareSingleReportProps> = ({
  reportTitle,
  reportId,
  reportUUID,
  onEditTitle,
  onSendEmails,
  onViewTrialReports,
  onBack,
  showEdit = false,
}) => {
  const [linkCopied, setLinkCopied] = useState(false);
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  const reportUrl = `${BaseURL}${reportUUID ?? ""}`;
  const trackReportsEvents = useTrackingEvent({
    page: TrialReportAmplitudeEvent.TRIAL_REPORTS_PAGE,
    group: TrialReportAmplitudeEvent.TRIAL_REPORTS_GROUP,
    module: TrialReportAmplitudeEvent.TRIAL_REPORTS_GROUP,
  });

  const handleCopyLink: () => void = () => {
    void navigator.clipboard.writeText(reportUrl);
    void trackReportsEvents({
      event: TrialReportAmplitudeEvent.COPY_REPORT_LINK,
      originAction: "Copy Link",
    });
    setLinkCopied(true);
    setTimeout(() => {
      setLinkCopied(false);
    }, 3000);
  };

  const handleEditClick = (): void => {
    void trackReportsEvents({
      event: TrialReportAmplitudeEvent.EDIT_TRIAL_REPORT,
      originAction: "Edit Report",
    });
    onBack?.();
  };

  return (
    <Flex vertical gap="large" className="trial-report-drawer">
      {/* Report Title Section */}
      <div className="report-title-container">
        <Flex
          justify="space-between"
          align="center"
          style={{
            backgroundColor: "#f3f4f6",
            padding: "8px 12px",
            borderRadius: 0,
            marginBottom: 10,
          }}
        >
          <Text strong>
            <Flex align="center" gap="small">
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.75 7.5C6.75 7.08579 7.08579 6.75 7.5 6.75H10.5C10.9142 6.75 11.25 7.08579 11.25 7.5V10.5C11.25 10.9142 10.9142 11.25 10.5 11.25H7.5C7.08579 11.25 6.75 10.9142 6.75 10.5V7.5Z"
                  fill="#696F88"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M3.75 2.25H5.25V3.75L12.75 3.75V2.25H14.25V3.75L15.75 3.75V5.25H14.25V12.75H15.75V14.25H14.25V15.75H12.75V14.25L5.25 14.25V15.75H3.75V14.25H2.25V12.75H3.75V5.25H2.25V3.75L3.75 3.75V2.25ZM5.25 12.75V5.25L12.75 5.25V12.75L5.25 12.75Z"
                  fill="#696F88"
                />
              </svg>
              {reportTitle ?? "Untitled Report"}
            </Flex>
          </Text>
          {showEdit ? (
            <Button
              className="edit-btn"
              type="text"
              onClick={onBack}
              style={{ color: "#1890ff" }}
            >
              Edit
            </Button>
          ) : (
            <Button
              className="edit-btn"
              type="text"
              onClick={() => {
                handleEditClick();
              }}
              style={{ color: "#1890ff" }}
            >
              Edit
            </Button>
          )}
        </Flex>
      </div>

      {/* View Report Section */}
      <div className="single-report-container">
        <Flex vertical gap="small">
          <h4 style={{ marginBottom: 8 }}>
            {formatTranslation(t("report.view.report"))}
          </h4>
          <Flex align="center" justify="space-between" gap="middle">
            <Text type="secondary" style={{ color: "#696F88" }}>
              {formatTranslation(t("report.view.next"))}
            </Text>
            <Button
              className="next-btn"
              icon={<ExportOutlined />}
              onClick={() => {
                window.open(reportUrl, "_blank");
                void trackReportsEvents({
                  event: TrialReportAmplitudeEvent.OPEN_REPORT_LINK,
                  originAction: "Open Link",
                });
              }}
              style={{ marginTop: 8 }}
            >
              {formatTranslation(t("report.open.link"))}
            </Button>
          </Flex>
        </Flex>
        <hr
          style={{
            border: "none",
            borderTop: "1px solid #f0f0f0",
            margin: "16px 0",
          }}
        />
        {/* Direct Link Section */}
        <Flex vertical gap="small">
          <h4 style={{ marginBottom: 8 }}>Direct link</h4>
          <Flex align="center" gap="middle">
            <Text type="secondary" style={{ flex: 1, color: "#696F88" }}>
              {formatTranslation(t("report.share.link"))}
            </Text>
            {linkCopied ? (
              <div className="link-copied">
                <CheckOutlined style={{ marginRight: 4 }} />
                {formatTranslation(t("report.link.link"))}
              </div>
            ) : (
              <Button
                icon={<LinkOutlined />}
                onClick={handleCopyLink}
                style={{ marginTop: 8 }}
              >
                {formatTranslation(t("report.copy.link"))}
              </Button>
            )}
          </Flex>
        </Flex>
        <hr
          style={{
            border: "none",
            borderTop: "1px solid #f0f0f0",
            margin: "16px 0",
          }}
        />
        {/* Email Section */}
        <EmailInputWithTags
          onSendEmails={(emails) => {
            void onSendEmails?.(emails);
          }}
        />
      </div>
    </Flex>
  );
};
